/* Copyright (c) 2020-present CloudIO Inc */

import { DataRecord } from '@cloudio-saas/datasource-types';
import { TextField, TextFieldProps, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { BaseProps, DateField, Form } from 'componentregistry';
import {
  useAttributeDirty,
  useCurrentDataStore,
  useIsEditable,
  useStringAttribute,
} from 'components/DataStoreReference/DataStoreReference';
import { HorizontalFormField } from 'components/Form/FormUtils';
import { FieldWrapper, useFieldVariant } from 'components/TextField/BaseField';
import zIndex from 'constants/ZIndex';
import { useSyncControllerFunction } from 'controller/ControllerRegistry';
import { DateInputWithTZ } from 'custom/DateInputWithTZ';
import { isValid } from 'date-fns';
import { useStringExpression } from 'expression/useExpression';
import React, { useId, useState } from 'react';
import { useUserDateFormat, useUserDateTimeFormat } from 'root/useUser';
import { useErrorMessage } from 'store/useAppStore';
import { useTheme } from 'theme/Theme';
import useTrigger from 'triggers/useTrigger';
import { useAudit } from '../../root/Audit';
import { useTranslate } from '../../utils/lang';
import { useFocus } from 'hooks/useFocus';
import { format as formatDate } from 'date-fns';

export const useDateFieldStyles = makeStyles((theme: Theme) => ({
  popper: {
    zIndex: zIndex.AutoCompletePopper,
    '& .MuiDateTimePickerTabs-tabs': {
      color: '#fff',
    },
  },
  overflowHidden: {
    overflow: 'hidden',
  },
  dateField: {
    '& .MuiIconButton-root': {
      marginRight: 0,
    },
  },
}));

interface DateFieldProps
  extends Required<DateField.DateField>,
    BaseProps<'DateField'> {}

interface Props extends DateFieldProps, Form.HorizontalFormProps {}

function IODateField(
  props: Props,
  ref: React.Ref<HTMLInputElement>,
): React.ReactElement {
  const {
    appUid,
    autoFocus,
    roleUid,
    className,
    children,
    domProps,
    flex,
    formHorizontal,
    format,
    insertable,
    itemId,
    labelAlignment,
    labelColor,
    labelWidth,
    onBlurFunction,
    onChangeFunction,
    onFocusFunction,
    onKeyDownFunction,
    disableDateFunction,
    pageId,
    platform,
    required,
    showTime,
    hideTodayButton,
    serverValidateOnChange,
    updatable,
    disablePast,
    plain,
    viewAttribute,
    placeholder,
    bgColorExpression,
    colorExpression,
    textFontSize,
    textBold,
    textItalic,
    labelItalic,
    labelBold,
    minDateAttribute,
    maxDateAttribute,
  } = props;

  let { helperText } = props;
  const { label } = props;
  const [focused, setFocused] = useState(false);
  const { t } = useTranslate();
  helperText = t(helperText);
  let labelResult =
    useStringExpression({
      exp: label,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'label',
        data: {},
      },
    }) || undefined;
  labelResult = labelResult ? t(labelResult) : '';
  const classes = useDateFieldStyles();
  const variant = useFieldVariant(appUid);
  const [value, setValue] = useStringAttribute(viewAttribute);
  const [minDateString] = useStringAttribute(minDateAttribute);
  const [maxDateString] = useStringAttribute(maxDateAttribute);
  const store = useCurrentDataStore<DataRecord>();
  const attr = store?.getAttribute(viewAttribute);
  const isStringDateTime = attr?.attributeType === 'StringDateTime';
  const dateOnly = attr?.attributeType === 'Date';
  const handleDateChange = async (date: Date | null) => {
    const oldValue = value;
    let newValue =
      date && isValid(date)
        ? isStringDateTime
          ? formatDate(date, "yyyy-MM-dd'T'HH:mm:ss")
          : dateOnly
          ? formatDate(date, 'yyyy-MM-dd')
          : date.toISOString()
        : '';
    const result = onChangeCallback({
      oldValue,
      value: newValue,
      event: undefined,
      eventName: 'onChange',
    });
    const currentRecordId = store?.getCurrentRecordId();
    if (currentRecordId && store?.hasError(viewAttribute, currentRecordId)) {
      store?.clearError(viewAttribute, currentRecordId);
    }
    if (result.status === 'ERROR') return;
    if (result.status === 'SUCCESS' && typeof result.result === 'string') {
      newValue = result.result;
    }
    setValue(newValue);
    await onChangeTrigger?.(undefined, {
      oldValue: value,
      value: newValue,
    }).then((r) => {
      if (!r) {
        setValue(oldValue);
      }
    });
    if (serverValidateOnChange && store) {
      await store.serverValidate({ attribute: viewAttribute, oldValue });
      await afterServerValidateTrigger?.();
    }
    if (platform.isRecording()) {
      platform.recordLine(itemId, `await ui.type('${itemId}', new Date());`);
    }
  };
  const errMsgMinMaxDate =
    maxDateString &&
    minDateString &&
    new Date(minDateString) > new Date(maxDateString)
      ? 'Min Date must be less than Max Date!'
      : '';
  const errorMessage = useErrorMessage(store, viewAttribute);
  const dirty = useAttributeDirty(viewAttribute);
  const dateFormat = useUserDateFormat();
  const dateTimeFormat = useUserDateTimeFormat();
  const labelColorString =
    useStringExpression({
      exp: labelColor,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'labelColor',
        data: {},
      },
    }) || undefined;

  const bgColorString =
    useStringExpression({
      exp: bgColorExpression,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'bgColorExpression',
        data: {},
      },
    }) || undefined;
  const textColorString =
    useStringExpression({
      exp: colorExpression,
      context: {
        appUid,
        pageId,
        itemId,
        property: 'colorExpression',
        data: {},
      },
    }) || undefined;

  const disabled = !useIsEditable({
    appUid,
    pageId,
    itemId,
    insertable,
    updatable,
  });

  const [onChangeTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnChange',
  );
  const [onFocusTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnFocus',
  );
  const [onBlurTrigger] = useTrigger(
    {
      platform,
      roleUid,
      appUid,
      pageId,
      itemId,
      disableProgressTracking: true,
    },
    'TriggerOnBlur',
  );
  const [afterServerValidateTrigger] = useTrigger(
    { platform, appUid, roleUid, pageId, itemId },
    'TriggerAfterServerValidate',
  );
  const onChangeCallback = useSyncControllerFunction<string>({
    appUid,
    pageId,
    itemId,
    functionName: onChangeFunction,
  });
  const onKeyDownCallback = useSyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: onKeyDownFunction,
  });
  const onFocusCallback = useSyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: onFocusFunction,
  });
  const onBlurCallback = useSyncControllerFunction({
    appUid,
    pageId,
    itemId,
    functionName: onBlurFunction,
  });
  const disableDateCallback = useSyncControllerFunction<boolean>({
    appUid,
    pageId,
    itemId,
    functionName: disableDateFunction,
  });
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlurCallback({ event, eventName: 'onBlur' });
    onBlurTrigger?.(event);
  };

  const theme = useTheme();
  const id = useId();
  const inputRef = useFocus(itemId);
  const textFieldProps = (p: JSX.IntrinsicAttributes & TextFieldProps) => {
    return {
      ...p,
      id,
      onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        onFocusCallback({ event, eventName: 'onFocus' });
        onFocusTrigger?.(event);
      },
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
        onKeyDownCallback({ event, eventName: 'onKeyDown' });
      },
      inputProps: {
        'data-testid': `${itemId}-dateField`,
        className: clsx('date', p.inputProps?.className),
        ...p.inputProps,
        placeholder: placeholder || p.inputProps?.placeholder,
        sx: {
          '&.Mui-disabled': {
            WebkitTextFillColor: `${textColorString} !important`,
          },
        },
      },
      InputProps: {
        ...p.InputProps,
        style: {
          color: textColorString,
          backgroundColor: bgColorString,
          fontSize: textFontSize,
          fontWeight: textBold ? 800 : undefined,
          fontStyle: textItalic ? 'italic' : undefined,
          '&.Mui-disabled': {
            backgroundColor: bgColorString,
          },
        },
        sx: plain
          ? {
              ...p.InputProps?.sx,
              '&.Mui-disabled': {
                '& > input': {
                  color: 'text.primary',
                  WebkitTextFillColor: theme.palette.text.primary,
                  paddingTop: 0,
                },
                '&:before': {
                  border: 0,
                },
                '& > .MuiInputAdornment-positionEnd': {
                  display: 'none',
                },
              },
            }
          : p.InputProps?.sx,
      },
      InputLabelProps: {
        sx: {
          color: labelColorString,
          fontWeight: labelBold ? 800 : undefined,
          fontStyle: labelItalic ? 'italic' : undefined,
          '&.Mui-disabled': {
            color: labelColorString,
          },
        },
      },
    };
  };

  const tip =
    errorMessage || errMsgMinMaxDate
      ? {
          'data-tip': errorMessage || errMsgMinMaxDate,
          'data-tip-at': 'top',
          'data-tip-error': true,
          'data-tip-delay': 0,
        }
      : undefined;

  const dateFieldProps = {
    renderInput: (p: JSX.IntrinsicAttributes & TextFieldProps) => (
      <TextField
        fullWidth
        autoFocus={autoFocus}
        helperText={errorMessage || errMsgMinMaxDate || helperText}
        variant={variant}
        {...textFieldProps(p)}
        {...tip}
        required={required}
        error={!!errorMessage || !!errMsgMinMaxDate}
      />
    ),
    inputRef,
    // @ts-ignore PopperProps
    PopperProps: {
      className: clsx(classes.popper, 'custom-datepicker-dialog'),
    },
    inputFormat: format || (showTime ? dateTimeFormat : dateFormat),
    value: value || null,
    onChange: handleDateChange,
    disabled,
    required,
    showTime,
    disablePast,
    hideTodayButton,
    onClose: onBlur,
    autoFocus,
    attr,
    minDateString,
    maxDateString,
    shouldDisableDate: (date: Date) => {
      if (!disableDateFunction) return false;
      const returnedResult = disableDateCallback({ date });
      if (
        returnedResult.status === 'SUCCESS' &&
        typeof returnedResult.result === 'boolean'
      ) {
        return returnedResult.result;
      }
      return false;
    },
  };
  useAudit({ appUid, pageId, active: focused, viewAttribute });

  return (
    <FieldWrapper
      className={clsx(className, classes.overflowHidden, classes.dateField)}
      dirty={dirty}
      domProps={domProps}
      flex={flex}
      ref={ref}
    >
      {formHorizontal ? (
        <HorizontalFormField
          control={<DateInputWithTZ {...dateFieldProps} />}
          label={labelResult}
          labelAlignment={labelAlignment}
          labelWidth={labelWidth}
          required={required}
          labelColor={labelColorString}
          fontWeight={labelBold ? 800 : undefined}
          fontStyle={labelItalic}
          id={id}
        />
      ) : (
        <DateInputWithTZ {...dateFieldProps} label={labelResult} />
      )}
      {children}
    </FieldWrapper>
  );
}

export default React.forwardRef(IODateField);
